import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ExposeCard from '@/components/base/ExposeCard';
import { useSeo } from '@/hooks/useSeo';
import { getAllExposes } from '@/lib/content';
import { verdictMeta, verdictOrder } from '@/lib/verdict';
import { categoryInfos } from '@/mocks/exposes';
import type { VerdictKey } from '@/types/content';

export default function Ranking() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const category = searchParams.get('category') || 'all';
  const verdict = searchParams.get('verdict') || 'all';
  const sort = (searchParams.get('sort') as 'heat' | 'latest') || 'heat';
  const [keyword, setKeyword] = useState(q);

  useSeo({
    title: '热料榜单 · 全部爆料核实列表 | 看料爆料',
    description:
      '看料爆料热料榜单汇总全部已核实爆料，支持按分类、判定和关键词筛选，实时查看看料爆料真假、实锤证据与辟谣结果。',
    keywords: '看料爆料榜单,看料爆料真假,爆料核实列表,看料爆料实锤,看料爆料辟谣',
    type: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '热料榜单',
        description: '看料爆料全部爆料核实记录列表',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '' },
          { '@type': 'ListItem', position: 2, name: '热料榜单' },
        ],
      },
    ],
  });

  const list = useMemo(() => {
    const keywordLower = q.trim().toLowerCase();
    let result = getAllExposes().filter((item) => {
      if (category !== 'all' && item.categorySlug !== category) return false;
      if (verdict !== 'all' && item.verdict !== verdict) return false;
      if (keywordLower) {
        const haystack = `${item.title}${item.summary}${item.tags.join('')}${item.category}`.toLowerCase();
        if (!haystack.includes(keywordLower)) return false;
      }
      return true;
    });
    result = result.sort((a, b) =>
      sort === 'heat' ? b.heat - a.heat : a.publishedAt < b.publishedAt ? 1 : -1,
    );
    return result;
  }, [q, category, verdict, sort]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParam('q', keyword.trim());
  };

  const resetAll = () => {
    setKeyword('');
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const hasFilter = Boolean(q) || category !== 'all' || verdict !== 'all';

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100">
          <div className="container-page py-8 md:py-10">
            <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '热料榜单' }]} />
            <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">热料榜单</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              这里汇总了全部爆料核实记录。可以按关键词搜索，也可以按分类与判定筛选，快速找到你想确认的那条料。
            </p>
          </div>
        </div>

        <div className="container-page py-8 md:py-10">
          <form onSubmit={handleSearch} className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入关键词，例如「综艺」「代言费」「预警」"
                aria-label="搜索爆料"
                className="w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-3.5 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-search-eye-line"></i>
              </span>
              搜索核实记录
            </button>
          </form>

          <div className="mt-6 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500 mr-1">分类</span>
              <button
                type="button"
                onClick={() => updateParam('category', 'all')}
                className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                  category === 'all'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {categoryInfos.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => updateParam('category', cat.slug)}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                    category === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500 mr-1">判定</span>
              <button
                type="button"
                onClick={() => updateParam('verdict', 'all')}
                className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                  verdict === 'all'
                    ? 'bg-foreground-900 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {verdictOrder.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => updateParam('verdict', key)}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                    verdict === key
                      ? 'bg-foreground-900 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {verdictMeta[key as VerdictKey].short}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-4">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-semibold text-foreground-950">{list.length}</span> 条核实记录
              {q && <span className="text-foreground-500"> · 关键词「{q}」</span>}
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500">排序</span>
              <button
                type="button"
                onClick={() => updateParam('sort', 'heat')}
                className={`rounded-md px-3 py-1.5 text-xs transition-colors cursor-pointer ${
                  sort === 'heat' ? 'bg-primary-100 text-primary-700' : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                按热度
              </button>
              <button
                type="button"
                onClick={() => updateParam('sort', 'latest')}
                className={`rounded-md px-3 py-1.5 text-xs transition-colors cursor-pointer ${
                  sort === 'latest' ? 'bg-primary-100 text-primary-700' : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                按最新
              </button>
              {hasFilter && (
                <button
                  type="button"
                  onClick={resetAll}
                  className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-refresh-line"></i>
                  </span>
                  重置
                </button>
              )}
            </div>
          </div>

          {list.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((item) => (
                <ExposeCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="mt-10 rounded-xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="mx-auto flex w-14 h-14 items-center justify-center rounded-full bg-background-200 text-foreground-500 text-2xl">
                <i className="ri-search-eye-line"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">没有匹配的核实记录</h2>
              <p className="mt-2 text-sm text-foreground-600">
                换个关键词试试，或者清空筛选条件看看全部记录。
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部记录
              </button>
            </div>
          )}

          <div className="mt-12 rounded-xl border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950">没找到你关心的那条料？</h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600">
              把你看到的爆料发给我们，注明来源与时间点，我们会尽快核实并更新判定结果。
            </p>
            <Link
              to="/about#submit"
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-send-plane-line"></i>
              </span>
              提交核实线索
            </Link>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}